import { fetchData, setHeaderOptions } from "#/api/apiHelpers";

export type OutreachSettingsMode = "daily" | "weekly";

export type OutreachSettings = {
  id: string;
  enabled: boolean;
  mode: OutreachSettingsMode;
  target_hour: number;
  target_day_of_week: number | null;
  min_interval_days: number;
  created_at?: string;
  updated_at?: string;
};

export type CreateOutreachSettingsRequest = Partial<
  Pick<
    OutreachSettings,
    | "enabled"
    | "mode"
    | "target_hour"
    | "target_day_of_week"
    | "min_interval_days"
  >
>;

export type UpdateOutreachSettingsRequest = CreateOutreachSettingsRequest;

const BASE_ROUTE = "/outreach-settings";

export async function listOutreachSettings(
  options?: { signal?: AbortSignal }
): Promise<OutreachSettings[]> {
  const request = setHeaderOptions({ method: "GET" });
  return fetchData<OutreachSettings[]>(BASE_ROUTE, {
    ...request,
    signal: options?.signal,
  });
}

export async function getOutreachSettingsById(
  id: string,
  options?: { signal?: AbortSignal }
): Promise<OutreachSettings> {
  if (!id) {
    throw new Error("Outreach settings id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<OutreachSettings>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export async function createOutreachSettings(
  payload: CreateOutreachSettingsRequest
): Promise<OutreachSettings> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });

  return fetchData<OutreachSettings>(BASE_ROUTE, request);
}

export async function updateOutreachSettings(
  id: string,
  payload: UpdateOutreachSettingsRequest
): Promise<OutreachSettings> {
  if (!id) {
    throw new Error("Outreach settings id is required");
  }

  const request = setHeaderOptions({
    method: "PUT",
    body: payload,
  });

  return fetchData<OutreachSettings>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}

export async function deleteOutreachSettings(
  id: string
): Promise<{ id: string }> {
  if (!id) {
    throw new Error("Outreach settings id is required");
  }

  const request = setHeaderOptions({ method: "DELETE" });
  return fetchData<{ id: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(id)}`,
    request
  );
}
